import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import logger from '../utils/logger';
import { useAuth } from './AuthProvider';
import apiService from '../services/api';
import { hasMatchingAccount, normalizeAccountKey, resolveCanonicalAccount } from '../utils/accountUtils';

export interface UserRole {
  super_admin: 'super_admin';
  admin: 'admin';
  user: 'user';
  viewer: 'viewer';
}

export interface Permission {
  // Global permissions
  'users.manage': 'users.manage';
  'users.view': 'users.view';
  'accounts.manage': 'accounts.manage';
  'accounts.view': 'accounts.view';
  'system.admin': 'system.admin';
  
  // Account-specific permissions
  'prospects.upload': 'prospects.upload';
  'prospects.view': 'prospects.view';
  'prospects.manage': 'prospects.manage';
  'campaigns.view': 'campaigns.view';
  'campaigns.manage': 'campaigns.manage';
  'analytics.view': 'analytics.view';
  'reports.generate': 'reports.generate';
}

export interface UserProfile {
  uid: string;
  email: string;
  display_name: string;
  role: keyof UserRole;
  account_access: AccountAccess[];
  page_access: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AccountAccess {
  account: string;
  permissions: AccountPermissions;
}

export interface AccountPermissions {
  view_campaigns: boolean;
  sync_data: boolean;
  download_reports: boolean;
  upload_prospects: boolean;
  manage_budgets: boolean;
  manage_credentials: boolean;
}

export interface RBACContextValue {
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  
  // Permission checking
  hasPermission: (permission: keyof Permission, accountName?: string) => boolean;
  hasGlobalPermission: (permission: string) => boolean;
  hasAccountAccess: (accountName: string) => boolean;
  hasPageAccess: (pageId: string) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  
  // Account management
  accounts: string[];
  selectedAccount: string | null;
  setSelectedAccount: (account: string | null) => void;
  
  // User management (for admins)
  users: UserProfile[];
  availablePages: any[];
  
  // Actions
  refreshProfile: () => Promise<void>;
  loadUsers: () => Promise<void>;
  loadAvailablePages: () => Promise<void>;
  updateUserRole: (userId: string, role: keyof UserRole) => Promise<void>;
  grantAccountAccess: (userId: string, accountName: string) => Promise<void>;
  revokeAccountAccess: (userId: string, accountName: string) => Promise<void>;
  updateUserPageAccess: (email: string, pages: string[]) => Promise<void>;
  grantPageAccess: (email: string, pages: string[]) => Promise<void>;
  revokePageAccess: (email: string, pageId: string) => Promise<void>;
}

const RBACContext = createContext<RBACContextValue | undefined>(undefined);

export const RBACProvider = ({ children }: { children: ReactNode }) => {
  const { user, idToken, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [availablePages, setAvailablePages] = useState<any[]>([]);

  // Load user profile and initialize user in system
  const loadUserProfile = async () => {
    if (!user || !idToken) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Try to get existing profile
      let profile: UserProfile;
      try {
        profile = await apiService.getCurrentUser() as UserProfile;
      } catch (e: any) {
        if (e.response?.status === 404) {
          // User not in system, initialize them
          logger.debug('User not found, initializing...');
          await apiService.initializeUser({
            email: user.email,
            display_name: user.displayName || user.email,
            firebase_uid: user.uid
          });
          
          // Retry getting profile
          profile = await apiService.getCurrentUser() as UserProfile;
        } else {
          throw e;
        }
      }
      
      setUserProfile(profile);

      const rawAccountAccess: Array<AccountAccess | string | null | undefined> = Array.isArray(profile.account_access)
        ? profile.account_access
        : [];

      const normalizedAccountMap = new Map<string, string>();
      rawAccountAccess.forEach((access) => {
        const accountName = typeof access === 'string'
          ? access.trim()
          : (access?.account || '').trim();
        const normalizedKey = normalizeAccountKey(accountName);
        if (normalizedKey && !normalizedAccountMap.has(normalizedKey)) {
          normalizedAccountMap.set(normalizedKey, accountName);
        }
      });

      const normalizedAccounts = Array.from(normalizedAccountMap.values());

      setAccounts(normalizedAccounts);

      // Set or restore selected account safely when account access changes
      setSelectedAccount((currentSelection) => {
        if (normalizedAccounts.length === 0) {
          return null;
        }
        return resolveCanonicalAccount(currentSelection, normalizedAccounts, true) || normalizedAccounts[0];
      });
      
    } catch (e: any) {
      logger.error('Failed to load user profile:', e);
      setError(e.message || "We couldn't load your profile. Please refresh the page to continue.");
    } finally {
      setLoading(false);
    }
  };

  // Load users for admin interface
  const loadUsers = async () => {
    if (!userProfile || !isAdmin()) return;
    
    try {
      const userList = await apiService.getUsers() as UserProfile[];
      setUsers(userList);
    } catch (e: any) {
      logger.error('Failed to load users:', e);
      setError(e.message || 'Unable to load user list at this time. Please try again.');
    }
  };

  // Permission checking
  const hasPermission = (permission: keyof Permission, accountName?: string): boolean => {
    if (!userProfile) return false;
    
    // Super admins have all permissions
    if (userProfile.role === 'super_admin') return true;
    
    // Map backend snake_case permissions to frontend dot.notation
    const permissionMap: Record<string, string[]> = {
      'view_campaigns': ['campaigns.view', 'view_campaigns'],
      'sync_data': ['campaigns.manage', 'sync_data'],
      'download_reports': ['reports.generate', 'download_reports'],
      'upload_prospects': ['prospects.upload', 'upload_prospects'],
      'view_prospects': ['prospects.view', 'view_prospects'],
      'manage_prospects': ['prospects.manage', 'manage_prospects'],
      'view_analytics': ['analytics.view', 'view_analytics']
    };
    
    // Check role-based permissions
    switch (userProfile.role) {
      case 'admin':
        return true; // Admins have most permissions (except system.admin)
      case 'user':
        // Users have view and manage permissions for their accounts
        if (accountName) {
          const userAccounts = userProfile.account_access
            .map((access) => access.account)
            .filter((value): value is string => Boolean(value));
          return hasMatchingAccount(accountName, userAccounts);
        }
        
        // Check if permission is in allowed list (support both naming conventions)
        const allowedPermissions = [
          'prospects.view', 'campaigns.view', 'analytics.view',
          'view_prospects', 'view_campaigns', 'view_analytics',
          'prospects.upload', 'upload_prospects',
          'reports.generate', 'download_reports'
        ];
        
        // Check direct match or mapped match
        if (allowedPermissions.includes(permission)) return true;
        
        // Check if any mapped permission matches
        const mappedPerms = permissionMap[permission] || [];
        return mappedPerms.some(p => allowedPermissions.includes(p));
        
      case 'viewer':
        // Viewers only have view permissions
        if (accountName) {
          const userAccounts = userProfile.account_access
            .map((access) => access.account)
            .filter((value): value is string => Boolean(value));
          return hasMatchingAccount(accountName, userAccounts);
        }
        
        const viewerPermissions = [
          'prospects.view', 'campaigns.view', 'analytics.view',
          'view_prospects', 'view_campaigns', 'view_analytics'
        ];
        
        // Check direct match or mapped match
        if (viewerPermissions.includes(permission)) return true;
        
        // Check if any mapped permission matches
        const viewerMappedPerms = permissionMap[permission] || [];
        return viewerMappedPerms.some(p => viewerPermissions.includes(p));
        
      default:
        return false;
    }
  };

  const hasAccountAccess = (accountName: string): boolean => {
    if (!userProfile) return false;
    const userAccounts = userProfile.account_access
      .map((access) => access.account)
      .filter((value): value is string => Boolean(value));
    return hasMatchingAccount(accountName, userAccounts) || userProfile.role === 'super_admin';
  };

  const hasPageAccess = (pageId: string): boolean => {
    if (!userProfile) return false;
    
    // Super admins and admins have access to all pages
    if (userProfile.role === 'super_admin' || userProfile.role === 'admin') {
      return true;
    }
    
    // Check if user has explicit page access
    return userProfile.page_access?.includes(pageId) || false;
  };

  const isAdmin = (): boolean => {
    return userProfile?.role === 'admin' || userProfile?.role === 'super_admin';
  };

  const isSuperAdmin = (): boolean => {
    return userProfile?.role === 'super_admin';
  };

  const hasGlobalPermission = (permission: string): boolean => {
    if (!userProfile) return false;
    
    // Map common permission names to the Permission interface keys
    const permissionMap: Record<string, keyof Permission> = {
      'manage_users': 'users.manage',
      'view_users': 'users.view',
      'manage_clients': 'accounts.manage',
      'view_all_accounts': 'accounts.view',
      'system_admin': 'system.admin'
    };
    
    const mappedPermission = permissionMap[permission] || permission as keyof Permission;
    return hasPermission(mappedPermission);
  };

  // User management actions
  const updateUserRole = async (userId: string, role: keyof UserRole): Promise<void> => {
    if (!isSuperAdmin()) {
      throw new Error('Only super admins can update user roles');
    }
    
    await apiService.updateUserRole(userId, role);
    await loadUsers(); // Refresh user list
  };

  const grantAccountAccess = async (userId: string, accountName: string): Promise<void> => {
    if (!isAdmin()) {
      throw new Error('Admin privileges required');
    }
    
    await apiService.grantAccountAccess(userId, accountName);
    await loadUsers(); // Refresh user list
  };

  const revokeAccountAccess = async (userId: string, accountName: string): Promise<void> => {
    if (!isAdmin()) {
      throw new Error('Admin privileges required');
    }
    
    await apiService.revokeAccountAccess(userId, accountName);
    await loadUsers(); // Refresh user list
  };

  // Load available pages
  const loadAvailablePages = async (): Promise<void> => {
    try {
      const pages = await apiService.getAvailablePages();
      setAvailablePages(pages);
    } catch (e: any) {
      logger.error('Failed to load available pages:', e);
      setError(e.message || "We couldn't load your available pages. Please refresh to try again.");
    }
  };

  // Page access management
  const updateUserPageAccess = async (email: string, pages: string[]): Promise<void> => {
    if (!isAdmin()) {
      throw new Error('Admin privileges required');
    }
    
    await apiService.updateUserPageAccess(email, pages);
    await loadUsers(); // Refresh user list
  };

  const grantPageAccess = async (email: string, pages: string[]): Promise<void> => {
    if (!isAdmin()) {
      throw new Error('Admin privileges required');
    }
    
    await apiService.grantPageAccess(email, pages);
    await loadUsers(); // Refresh user list
  };

  const revokePageAccess = async (email: string, pageId: string): Promise<void> => {
    if (!isAdmin()) {
      throw new Error('Admin privileges required');
    }
    
    await apiService.revokePageAccess(email, pageId);
    await loadUsers(); // Refresh user list
  };

  const refreshProfile = async (): Promise<void> => {
    await loadUserProfile();
  };

  // Load profile when auth state changes
  useEffect(() => {
    if (!authLoading && user && idToken) {
      loadUserProfile();
    } else if (!user) {
      // Clear profile when user logs out
      setUserProfile(null);
      setAccounts([]);
      setSelectedAccount(null);
      setUsers([]);
      setLoading(false);
    }
  }, [user, idToken, authLoading]);

  // Load users when user profile is loaded and user is admin
  useEffect(() => {
    if (userProfile && isAdmin()) {
      loadUsers();
      loadAvailablePages();
    }
  }, [userProfile]);

  const contextValue: RBACContextValue = {
    userProfile,
    loading: loading || authLoading,
    error,
    hasPermission,
    hasGlobalPermission,
    hasAccountAccess,
    hasPageAccess,
    isAdmin,
    isSuperAdmin,
    accounts,
    selectedAccount,
    setSelectedAccount,
    users,
    availablePages,
    refreshProfile,
    loadUsers,
    loadAvailablePages,
    updateUserRole,
    grantAccountAccess,
    revokeAccountAccess,
    updateUserPageAccess,
    grantPageAccess,
    revokePageAccess
  };

  return (
    <RBACContext.Provider value={contextValue}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within RBACProvider');
  }
  return context;
};

// Permission checking hook for components
export const usePermission = (permission: keyof Permission, accountName?: string) => {
  const { hasPermission } = useRBAC();
  return hasPermission(permission, accountName);
};

// Account access hook
export const useAccountAccess = (accountName: string) => {
  const { hasAccountAccess } = useRBAC();
  return hasAccountAccess(accountName);
};

// Component wrapper for permission-based rendering
export const PermissionGate = ({ 
  permission, 
  accountName, 
  children, 
  fallback = null 
}: { 
  permission: keyof Permission;
  accountName?: string;
  children: ReactNode;
  fallback?: ReactNode;
}) => {
  const hasPermission = usePermission(permission, accountName);
  return hasPermission ? <>{children}</> : <>{fallback}</>;
};

// Role-based component wrapper
export const RoleGate = ({ 
  roles, 
  children, 
  fallback = null 
}: { 
  roles: (keyof UserRole)[];
  children: ReactNode;
  fallback?: ReactNode;
}) => {
  const { userProfile } = useRBAC();
  const hasRole = userProfile && roles.includes(userProfile.role);
  return hasRole ? <>{children}</> : <>{fallback}</>;
};
